import type { RenglonBorrador } from '@/lib/supabase/tipos-bd'

/**
 * LO QUE EL TÉCNICO LLEVA COTIZADO, EN SU TELÉFONO.
 *
 * Armar una cotización en la obra es media hora de trabajo: buscar treinta
 * materiales, poner cantidades, ajustar precios. Eso vivía solo en la memoria
 * del navegador y se guardaba contra el servidor; sin señal, el guardado
 * fallaba y al salir de la pantalla se perdía todo.
 *
 * Johan lo dijo sin medias tintas: «no puede perder». Así que cada cambio se
 * escribe también aquí, en el almacenamiento del propio teléfono, con o sin
 * cobertura. Si se cierra la aplicación, si se acaba la batería, si el
 * navegador descarta la pestaña: al volver, los renglones siguen ahí.
 *
 * Se guarda POR ORDEN DE SERVICIO y no por cotización, a propósito: sin señal
 * no existe todavía la cotización en la base —`fn_abrir_borrador` necesita
 * servidor—, y lo que hay que salvar es el trabajo, no el identificador.
 */

const LLAVE = 'mega.borrador.'

export type BorradorLocal = {
  ordenServicioId: string
  adicional: boolean
  lineas: RenglonBorrador[]
  /** Cuándo se tocó por última vez, para no pisar lo nuevo con lo viejo. */
  cuando: string
  /** Si ya existe en la base, su id. Null mientras no haya habido señal. */
  cotizacionId: string | null
  /** 0318: Herramientas que el técnico seleccionó en obra. */
  herramientas?: Record<string, number>
  /**
   * ¿Tiene cambios que el servidor todavía no tiene?
   *
   * La copia se escribe en cada cambio, con o sin señal, y la sincronización
   * la subía SIEMPRE: también cuando el autoguardado ya la había guardado.
   * Así pisaba lo que la oficina cambiara después y revivía los renglones
   * que el técnico había borrado (auditoría sin conexión, oct 2026). Ahora
   * solo sube la que está pendiente; al guardar en el servidor se marca.
   * Las viejas, sin el campo, cuentan como pendientes: es lo seguro.
   */
  pendiente?: boolean
}

export function guardarBorradorLocal(b: Omit<BorradorLocal, 'cuando' | 'pendiente'>): void {
  try {
    const dato: BorradorLocal = { ...b, cuando: new Date().toISOString(), pendiente: true }
    localStorage.setItem(LLAVE + b.ordenServicioId, JSON.stringify(dato))
  } catch {
    /* Sin espacio: no se puede hacer más, pero no se rompe la pantalla. */
  }
}

export function leerBorradorLocal(ordenServicioId: string): BorradorLocal | null {
  try {
    const crudo = localStorage.getItem(LLAVE + ordenServicioId)
    return crudo ? (JSON.parse(crudo) as BorradorLocal) : null
  } catch {
    return null
  }
}

/** El servidor ya tiene esta versión: no hay nada que subir. */
export function marcarBorradorGuardado(ordenServicioId: string): void {
  const local = leerBorradorLocal(ordenServicioId)
  if (!local) return
  try {
    localStorage.setItem(LLAVE + ordenServicioId, JSON.stringify({ ...local, pendiente: false }))
  } catch {
    /* Si no se puede marcar, a lo sumo se sube una vez de más. */
  }
}

export function olvidarBorradorLocal(ordenServicioId: string): void {
  try {
    localStorage.removeItem(LLAVE + ordenServicioId)
  } catch {
    /* Da igual: lo que manda es lo que esté en la base. */
  }
}

/** Todos los que esperan señal. Para poder subirlos y para poder contarlos. */
export function borradoresLocales(): BorradorLocal[] {
  const todos: BorradorLocal[] = []

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const clave = localStorage.key(i)
      if (!clave || !clave.startsWith(LLAVE)) continue
      const crudo = localStorage.getItem(clave)
      if (!crudo) continue
      todos.push(JSON.parse(crudo) as BorradorLocal)
    }
  } catch {
    /* Lo que se pueda leer. */
  }

  return todos
}
